import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { WikiTreeNode } from '@sd/shared';
import { api } from '../../lib/api.js';
import { type CatNode } from '../../lib/categories.js';
import { WORLD, iconForPath, catForIndex } from '../../lib/world.js';
import { DvIcon } from '../DvIcon.js';

/**
 * Кумулятивні лічильники піддерева по treePath (visit() вже повертає own+нащадки),
 * ключі знормалізовані до нижнього регістру — щоб casing tree_path не ламав збіг.
 */
function buildCountMap(roots: WikiTreeNode[]): Map<string, number> {
  const map = new Map<string, number>();
  function visit(n: WikiTreeNode): number {
    let total = n.items.length;
    for (const c of n.children) total += visit(c);
    const key = n.path.toLowerCase();
    map.set(key, (map.get(key) ?? 0) + total);
    return total;
  }
  for (const r of roots) visit(r);
  return map;
}

function countFor(path: string, counts: Map<string, number>): number {
  return counts.get(path.toLowerCase()) ?? 0;
}

const seg = (path: string): string => path.split('/').pop() ?? path;

/** Дерево-з-контенту → CatNode[]: пропускаємо _root і службові вузли (_aliases тощо). */
function treeToCat(nodes: WikiTreeNode[]): CatNode[] {
  return nodes
    .filter((n) => n.path !== '_root' && !seg(n.path).startsWith('_'))
    .map((n, i) => ({
      path: n.path,
      label: n.label || seg(n.path),
      icon: iconForPath(n.path),
      cat: catForIndex(i),
      type: '',
      children: n.children && n.children.length ? treeToCat(n.children) : undefined,
    }));
}

export function WikiSidebar(): JSX.Element {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const [nodes, setNodes] = useState<CatNode[]>(WORLD.navSource === 'canonical' ? WORLD.navTree : []);

  useEffect(() => {
    api.wikiTree().then((r) => {
      setCounts(buildCountMap(r.tree));
      if (WORLD.navSource === 'content') setNodes(treeToCat(r.tree));
    }).catch(() => setCounts(new Map()));
  }, []);

  return (
    <aside className="wiki-sidebar">
      <div className="wiki-tree-title">Дерево світу</div>
      {nodes.map((node, i) => (
        <TreeNode key={node.path} node={node} counts={counts} depth={0} cat={node.cat || catForIndex(i)} />
      ))}
    </aside>
  );
}

/** Рекурсивний вузол дерева. depth 0 — top (іконка в боксі), глибше — плоский, з відступом. */
function TreeNode({ node, counts, depth, cat }: { node: CatNode; counts: Map<string, number>; depth: number; cat: string }): JSX.Element {
  const { pathname } = useLocation();
  const children = node.children ?? [];
  const hasKids = children.length > 0;
  const [open, setOpen] = useState(depth === 0);
  const total = countFor(node.path, counts);
  const active = pathname === `/wiki/_/${node.path}`;
  const accent = `var(--${cat})`;

  if (depth === 0) {
    return (
      <div className="tree-node">
        <div className="tree-row">
          <Link to={`/wiki/_/${node.path}`} className="tree-link">
            <span className="tree-ico-box" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
              <DvIcon name={node.icon} size={16} color={accent} />
            </span>
            <span className="tree-label">{node.label}</span>
          </Link>
          {total > 0 && <span className="tree-count">{total}</span>}
          {hasKids && (
            <button type="button" className={`tree-caret ${open ? 'open' : ''}`} aria-expanded={open}
              aria-label={open ? 'Згорнути' : 'Розгорнути'} onClick={() => setOpen((o) => !o)}>
              <DvIcon name="chevron" size={14} />
            </button>
          )}
        </div>
        {hasKids && open && (
          <div className="tree-children">
            {children.map((c) => <TreeNode key={c.path} node={c} counts={counts} depth={1} cat={cat} />)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="tree-child-row" style={{ display: 'flex', alignItems: 'center', paddingLeft: (depth - 1) * 12 }}>
        <Link to={`/wiki/_/${node.path}`} className={`tree-child ${active ? 'active' : ''}`} style={{ flex: 1 }}>
          <span className="tree-cico"><DvIcon name={node.icon} size={14} /></span>
          <span className="tree-label">{node.label}</span>
          {total > 0 && <span className="tree-count">{total}</span>}
        </Link>
        {hasKids && (
          <button type="button" className={`tree-caret ${open ? 'open' : ''}`} aria-expanded={open}
            aria-label={open ? 'Згорнути' : 'Розгорнути'} onClick={() => setOpen((o) => !o)}>
            <DvIcon name="chevron" size={13} />
          </button>
        )}
      </div>
      {hasKids && open && children.map((c) => <TreeNode key={c.path} node={c} counts={counts} depth={depth + 1} cat={cat} />)}
    </div>
  );
}
